import {
    SliceFactory,
    FetchCollectionData,
    ApiCollectionResponse,
    FetchItemDataType,
} from '@europrocurement/l2d-redux-utils';
import {
    ConfigurationParameters as FormalityApiConfigurationParameters,
    Configuration,
    TachesEtRappelsDesDossierApi,
    type TachesEtRappelsDesDossierApiApiTacheDossierIdGetRequest,
    type TachesEtRappelsDesDossierApiApiTachesDossierGetCollectionRequest,
    type TachesEtRappelsDesDossierTacheRead,
} from '../../../openApi/ApiFormalite';
import { DOSSIER_TACHES_SLICE_NAME, FORMALITES_REDUCER_NAME } from '../constants';

export type DossierTachesApiObject = TachesEtRappelsDesDossierTacheRead & Record<string, unknown>;

export type DossierTaches = DossierTachesApiObject;

export const mapperDossierTaches = function (taches: DossierTaches) {
    return {
        ...taches,
    } as DossierTaches;
};

type dateFilters = {
    startDate?: string;
    endDate?: string;
};

export function createDossierTachesSlice(configuration: FormalityApiConfigurationParameters) {
    const conf = new Configuration(configuration);
    const api = new TachesEtRappelsDesDossierApi(conf);

    const fetchDossierTachesCollection: FetchCollectionData<DossierTaches> = ({
        pagination,
        filters,
        orders,
    }) => {
        const {
            tacheTitle,
            tacheIsDone,
            tacheAssignTo,
            tacheCreatedBy,
            dossierNumero,
            tacheExpiredAt,
            tacheCreatedAt,
            tachePriority,
        } = filters;

        let tacheExpiredAtAfter: string | undefined;
        let tacheExpiredAtBefore: string | undefined;
        let tacheCreatedAtAfter: string | undefined;
        let tacheCreatedAtBefore: string | undefined;

        let orderDossierNumero: 'asc' | 'desc' | undefined;
        let orderTacheAssignToPrenom: 'asc' | 'desc' | undefined;
        let orderTacheIsDone: 'asc' | 'desc' | undefined;
        let orderTacheTitle: 'asc' | 'desc' | undefined;
        let orderTacheCreatedAt: 'asc' | 'desc' | undefined;
        let orderTacheExpiredAt: 'asc' | 'desc' | undefined;

        // Range of date to filter
        if (tacheExpiredAt) {
            const filtersDate = tacheExpiredAt as dateFilters;
            const startedAt = filtersDate.startDate && new Date(filtersDate.startDate);
            if (startedAt) {
                startedAt.setHours(0, 0);
                tacheExpiredAtAfter = startedAt.toISOString();
            }

            const endedAt = filtersDate.endDate && new Date(filtersDate.endDate);
            if (endedAt) {
                endedAt.setHours(23, 59);
                tacheExpiredAtBefore = endedAt.toISOString();
            }
        }

        // Range of date to filter
        if (tacheCreatedAt) {
            const filtersDate = tacheCreatedAt as dateFilters;
            const startedAt = filtersDate.startDate && new Date(filtersDate.startDate);
            if (startedAt) {
                startedAt.setHours(0, 0);
                tacheCreatedAtAfter = startedAt.toISOString();
            }

            const endedAt = filtersDate.endDate && new Date(filtersDate.endDate);
            if (endedAt) {
                endedAt.setHours(23, 59);
                tacheCreatedAtBefore = endedAt.toISOString();
            }
        }

        orders.forEach((order) => {
            if (order.field === 'dossierNumero') {
                orderDossierNumero = order.value;
            }
            if (order.field === 'tacheAssignTo') {
                orderTacheAssignToPrenom = order.value;
            }
            if (order.field === 'tacheIsDone') {
                orderTacheIsDone = order.value;
            }
            if (order.field === 'tacheTitle') {
                orderTacheTitle = order.value;
            }
            if (order.field === 'tacheCreatedAt') {
                orderTacheCreatedAt = order.value;
            }
            if (order.field === 'tacheExpiredAt') {
                orderTacheExpiredAt = order.value;
            }
        });

        const requestParameters: TachesEtRappelsDesDossierApiApiTachesDossierGetCollectionRequest =
            {
                page: pagination.page + 1,
                itemsPerPage: 100,
                // Filters
                dossierNumero: dossierNumero as string,
                tacheIsDone: tacheIsDone as boolean,
                tacheTitle: tacheTitle as string,
                tacheAssignTo: tacheAssignTo as string,
                tacheCreatedBy: tacheCreatedBy as string,
                tachePriority: tachePriority as number,
                tacheExpiredAtAfter,
                tacheExpiredAtBefore,
                tacheCreatedAtAfter,
                tacheCreatedAtBefore,
                // Order
                orderDossierNumero,
                orderTacheAssignToPrenom,
                orderTacheIsDone,
                orderTacheTitle,
                orderTacheCreatedAt,
                orderTacheExpiredAt,
            };

        return api
            .apiTachesDossierGetCollection(requestParameters, undefined)
            .then((res) => res.data) as Promise<ApiCollectionResponse<DossierTaches>>;
    };

    const fetchDossierTachesItem: FetchItemDataType<DossierTaches> = ({ idItem }) => {
        const requestParameters: TachesEtRappelsDesDossierApiApiTacheDossierIdGetRequest = {
            id: String(idItem),
        };

        return api
            .apiTacheDossierIdGet(requestParameters)
            .then((res) => res.data) as Promise<DossierTaches>;
    };

    const { slice: dossierTachesSlice, dataSourcesThunks: dossierTachesDataSourcesThunks } =
        SliceFactory.createSlice<DossierTaches, DossierTaches>(
            DOSSIER_TACHES_SLICE_NAME,
            FORMALITES_REDUCER_NAME,
            [],
            fetchDossierTachesCollection,
            fetchDossierTachesItem,
            mapperDossierTaches,
            {},
            undefined,
            [{ field: 'tacheIsDone', value: 'asc' }],
        );

    return { dossierTachesSlice, dossierTachesDataSourcesThunks };
}
